import type {
  AssignmentDTO,
  EventDetailReportDTO,
  EventShowDTO,
} from "../../domain/contracts";
import { ROLE_FEE_KIND_LABELS } from "../../domain/contracts/assignments";
import { buildShowRosterRows } from "../../domain/services/show-assignment-matrix";
import {
  escapeCsvCell,
  formatCurrencyBucketsInline,
  formatPerformanceDateTime,
  formatPerformanceMoney,
} from "../ui/PerformanceReportsNav";

function downloadCsv(filename: string, rows: string[]) {
  const csv = rows.join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

function sanitizeFilenamePart(value: string) {
  return value.replace(/[^\w\-]+/g, "_").slice(0, 48);
}

export function downloadEventRosterCsv(input: {
  eventId: string;
  eventTitle: string;
  assignments: AssignmentDTO[];
  eventShows: EventShowDTO[];
}) {
  const header = "ໂຊ,ວັນທີ,ນັກສະແດງ,ບົດບາດ";
  const rosterRows = buildShowRosterRows(input.assignments, input.eventShows);
  const lines = [header];

  for (const show of rosterRows) {
    if (show.performers.length === 0) {
      lines.push(
        [
          escapeCsvCell(show.showLabel),
          escapeCsvCell(formatPerformanceDateTime(show.showDate)),
          "",
          "",
        ].join(","),
      );
      continue;
    }

    for (const performer of show.performers) {
      lines.push(
        [
          escapeCsvCell(show.showLabel),
          escapeCsvCell(formatPerformanceDateTime(show.showDate)),
          escapeCsvCell(performer.performerName),
          escapeCsvCell(
            performer.roles
              .map(
                (role) =>
                  ROLE_FEE_KIND_LABELS[
                    role as keyof typeof ROLE_FEE_KIND_LABELS
                  ] ?? role,
              )
              .join(" · "),
          ),
        ].join(","),
      );
    }
  }

  downloadCsv(
    `event-roster-${sanitizeFilenamePart(input.eventTitle)}-${input.eventId.slice(0, 8)}.csv`,
    lines,
  );
}

export function downloadEventCostBreakdownCsv(input: {
  eventId: string;
  eventTitle: string;
  assignments: AssignmentDTO[];
  totalEventShowCount: number;
}) {
  const header =
    "ນັກສະແດງ,ບົດບາດ,ບັນທັດ,ໂຊ,ຊ້ອມ(ວັນ),Gross,ບາງໂຊ";
  const lines = [header];

  const sorted = [...input.assignments].sort((a, b) => {
    const name = (a.performerName ?? a.performerId).localeCompare(
      b.performerName ?? b.performerId,
    );
    if (name !== 0) return name;
    return a.lineKey.localeCompare(b.lineKey);
  });

  for (const row of sorted) {
    const selectedCount =
      row.eventShowIds && row.eventShowIds.length > 0
        ? row.eventShowIds.length
        : row.showCount;
    const partial =
      input.totalEventShowCount >= 2 &&
      selectedCount > 0 &&
      selectedCount < input.totalEventShowCount;

    lines.push(
      [
        escapeCsvCell(row.performerName ?? row.performerId),
        escapeCsvCell(
          ROLE_FEE_KIND_LABELS[row.roleFeeKind] ?? row.roleFeeKind,
        ),
        escapeCsvCell(row.lineKey),
        escapeCsvCell(selectedCount),
        escapeCsvCell(row.rehearsalDays),
        escapeCsvCell(row.grossAmount),
        escapeCsvCell(partial ? "yes" : "no"),
      ].join(","),
    );
  }

  downloadCsv(
    `event-cost-${sanitizeFilenamePart(input.eventTitle)}-${input.eventId.slice(0, 8)}.csv`,
    lines,
  );
}

const DOCUMENT_TYPE_LABELS = {
  quotation: "QT",
  invoice: "INV",
  receipt: "RCPT",
} as const;

export function downloadEventFinancialSnapshotCsv(input: {
  eventId: string;
  eventTitle: string;
  report: EventDetailReportDTO;
}) {
  const { report } = input;
  const summaryHeader = "ປະເພດ,ຄ່າ";
  const summaryRows = [
    summaryHeader,
    ["ງານ", escapeCsvCell(report.eventTitle)].join(","),
    ["ນັກສະແດง (ບັນທັດ)", escapeCsvCell(report.assignmentCount)].join(","),
    ["ໃບສະເໜີລາຄາ", escapeCsvCell(formatCurrencyBucketsInline(report.quoted))].join(
      ",",
    ),
    ["ໃບເກັບເງິນ", escapeCsvCell(formatCurrencyBucketsInline(report.invoiced))].join(
      ",",
    ),
    ["ເກັບແລ້ວ", escapeCsvCell(formatCurrencyBucketsInline(report.collected))].join(
      ",",
    ),
    [
      "AR ຄ້າງ",
      escapeCsvCell(formatCurrencyBucketsInline(report.outstandingAr)),
    ].join(","),
    [
      "ຈ່າຍແລ້ວ",
      escapeCsvCell(formatPerformanceMoney(report.payoutPaid, "LAK")),
    ].join(","),
    [
      "AP ຄ້າງ",
      escapeCsvCell(formatPerformanceMoney(report.payoutOutstanding, "LAK")),
    ].join(","),
    [
      "ກຳໄລ (ເງິນຈິງ)",
      escapeCsvCell(
        report.profitLoss.realized.profit == null
          ? ""
          : formatPerformanceMoney(report.profitLoss.realized.profit, "LAK"),
      ),
    ].join(","),
    [
      "ກຳໄລ% (ເງິນຈິງ)",
      escapeCsvCell(report.profitLoss.realized.profitPercent ?? ""),
    ].join(","),
    [
      "ກຳໄລ (ຄາດການ)",
      escapeCsvCell(
        report.profitLoss.projected.profit == null
          ? ""
          : formatPerformanceMoney(report.profitLoss.projected.profit, "LAK"),
      ),
    ].join(","),
    [
      "ກຳໄລ% (ຄາດການ)",
      escapeCsvCell(report.profitLoss.projected.profitPercent ?? ""),
    ].join(","),
    [
      "ຕົ້ນທຸນ commit",
      escapeCsvCell(
        formatPerformanceMoney(
          report.profitLoss.costBreakdown.totalCommitted,
          "LAK",
        ),
      ),
    ].join(","),
    [
      "ນັກສະແດງ commit",
      escapeCsvCell(
        formatPerformanceMoney(
          report.profitLoss.costBreakdown.performerCommitted,
          "LAK",
        ),
      ),
    ].join(","),
    [
      "Staff commit",
      escapeCsvCell(
        formatPerformanceMoney(
          report.profitLoss.costBreakdown.staffCommitted,
          "LAK",
        ),
      ),
    ].join(","),
    [
      "Prop commit",
      escapeCsvCell(
        formatPerformanceMoney(
          report.profitLoss.costBreakdown.productionCommitted,
          "LAK",
        ),
      ),
    ].join(","),
    [
      "Outsource commit",
      escapeCsvCell(
        formatPerformanceMoney(
          report.profitLoss.costBreakdown.outsourceCommitted,
          "LAK",
        ),
      ),
    ].join(","),
    [
      "Outsource paid",
      escapeCsvCell(
        formatPerformanceMoney(
          report.profitLoss.costBreakdown.outsourcePaid,
          "LAK",
        ),
      ),
    ].join(","),
    [
      "ຄ່ານາຍໜ້າ commit",
      escapeCsvCell(
        formatPerformanceMoney(
          report.profitLoss.costBreakdown.commissionCommitted,
          "LAK",
        ),
      ),
    ].join(","),
    [
      "ກຳໄລ (legacy margin)",
      escapeCsvCell(
        report.margin == null
          ? ""
          : formatPerformanceMoney(report.margin, "LAK"),
      ),
    ].join(","),
    [
      "fx_usd_to_lak",
      escapeCsvCell(report.profitLoss.fxRates?.fxUsdToLak ?? ""),
    ].join(","),
    [
      "fx_thb_to_lak",
      escapeCsvCell(report.profitLoss.fxRates?.fxThbToLak ?? ""),
    ].join(","),
    [
      "fx_rate_note",
      escapeCsvCell(report.profitLoss.fxRates?.fxRateNote ?? ""),
    ].join(","),
    [
      "revenue_lak_equivalent_collected",
      escapeCsvCell(
        report.profitLoss.converted?.revenueCollected.lakEquivalent == null
          ? ""
          : report.profitLoss.converted.revenueCollected.lakEquivalent,
      ),
    ].join(","),
    [
      "profit_realized_lak_equivalent",
      escapeCsvCell(
        report.profitLoss.converted?.realized.profit == null
          ? ""
          : report.profitLoss.converted.realized.profit,
      ),
    ].join(","),
    [
      "profit_projected_lak_equivalent",
      escapeCsvCell(
        report.profitLoss.converted?.projected.profit == null
          ? ""
          : report.profitLoss.converted.projected.profit,
      ),
    ].join(","),
    [
      "% ເກັບໄດ້ (LAK)",
      escapeCsvCell(report.quotedFulfillmentPercent ?? ""),
    ].join(","),
    [
      "ໂຊ same-day discount",
      escapeCsvCell(
        report.sameDayDiscount.applies
          ? `${report.sameDayDiscount.discountedShowCount} ໂຊ`
          : "no",
      ),
    ].join(","),
    "",
    "ເອກະສານ,ເລກທີ,ປະເພດ,ສະຖານະ,ສະກຸນ,ຍອດ,ຊຳລະແລ້ວ,ຄ້າງ,ອອກເມື່ອ",
  ];

  for (const doc of report.documents) {
    summaryRows.push(
      [
        "doc",
        escapeCsvCell(doc.documentNo),
        escapeCsvCell(DOCUMENT_TYPE_LABELS[doc.documentType]),
        escapeCsvCell(doc.status),
        escapeCsvCell(doc.currency),
        escapeCsvCell(doc.grandTotal),
        escapeCsvCell(doc.paidAmount),
        escapeCsvCell(doc.remainingAmount),
        escapeCsvCell(
          doc.issuedAt ? formatPerformanceDateTime(doc.issuedAt) : "",
        ),
      ].join(","),
    );
  }

  downloadCsv(
    `event-financial-${sanitizeFilenamePart(input.eventTitle)}-${input.eventId.slice(0, 8)}.csv`,
    summaryRows,
  );
}
